
import React from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Eye, Check, X, Clock, ExternalLink, MoreHorizontal, CheckCircle } from "lucide-react";
import { Receivable, ReceivableStatus, StatusBadge } from "@/types/payments";
import { Link } from "react-router-dom";
import PaymentVerificationBadge from "@/components/shared/PaymentVerificationBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslations } from "use-intl";
import { useTaxPayableCaption } from "@/i18n/useTaxTransactionLabel";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";

interface ReceivablesTableProps {
  sortedReceivables: Receivable[];
  selectedReceivables: string[];
  toggleSelectReceivable: (receiveId: string) => void;
  toggleSelectAll: () => void;
  handleSort: (key: keyof Receivable) => void;
  renderSortIcon: (key: keyof Receivable) => React.ReactNode;
  handleView: (receiveId: string) => void;
  getStatusBadge: (status: ReceivableStatus) => StatusBadge;
  formatCurrency: (amount: number, currency: string) => string;
  filteredReceivables: Receivable[];
  hideCheckboxes?: boolean;
  highlightReceivableId?: string | null;
  onMarkAsReceived?: (receivable: Receivable) => void;
}

const ReceivablesTable = ({
  sortedReceivables,
  selectedReceivables,
  toggleSelectReceivable,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  handleView,
  getStatusBadge,
  formatCurrency,
  filteredReceivables,
  hideCheckboxes = false,
  highlightReceivableId,
  onMarkAsReceived,
}: ReceivablesTableProps) => {
  const t = useTranslations("consultantReceivables");
  const taxCaption = useTaxPayableCaption();
  const formatOptionalDate = useFormatOptionalDate();
  // Helper to render match status icon
  const renderMatchStatus = (receivable: Receivable) => {
    if (receivable.status !== "paid") return null;

    if (receivable.matched) {
      return (
        <div className="flex items-center">
          <Check className="h-4 w-4 text-green-500 mr-2" />
          {receivable.transactionId && (
            <Link
              to={`/bank?id=${receivable.transactionId}`}
              className="text-blue-500 hover:underline flex items-center"
            >
              {t("table.viewTransaction")}
              <ExternalLink className="ml-1 h-3 w-3" />
            </Link>
          )}
        </div>
      );
    }

    return <X className="h-4 w-4 text-red-500" />;
  };

  const getStatusIcon = (status: ReceivableStatus) => {
    switch (status) {
      case "paid": return <Check className="h-4 w-4 mr-1 text-green-500" />;
      case "manually_handled": return <CheckCircle className="h-4 w-4 mr-1 text-blue-500" />;
      case "unpaid": return <X className="h-4 w-4 mr-1 text-red-500" />;
      case "overdue": return <Clock className="h-4 w-4 mr-1 text-amber-500" />;
      default: return null;
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {!hideCheckboxes && (
              <TableHead className="w-12">
                <Checkbox 
                  checked={selectedReceivables.length === filteredReceivables.length && filteredReceivables.length > 0}
                  onCheckedChange={toggleSelectAll}
                  aria-label={t("table.selectAll")}
                />
              </TableHead>
            )}
            <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
              {t("table.columns.status")}
              {renderSortIcon('status')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('remittanceInformation')}>
              {t("table.columns.invoiceId")}
              {renderSortIcon('remittanceInformation')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('customer')}>
              {t("table.columns.customer")}
              {renderSortIcon('customer')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('amount')}>
              {t("table.columns.amount")}
              {renderSortIcon('amount')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('currency')}>
              {t("table.columns.currency")}
              {renderSortIcon('currency')}
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => handleSort('dueDate')}>
              {t("table.columns.dueDate")}
              {renderSortIcon('dueDate')}
            </TableHead>
            <TableHead>{t("table.columns.matched")}</TableHead>
            <TableHead>{t("table.columns.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedReceivables.map((receivable) => {
            const status = getStatusBadge(receivable.status);
            const isHighlighted = highlightReceivableId === receivable.id;
            return (
              <TableRow 
                key={receivable.id}
                className={`cursor-pointer hover:bg-muted/60 transition-colors ${isHighlighted ? "bg-blue-50 border-blue-200" : ""}`}
                onClick={() => handleView(receivable.id)}
              >
                {!hideCheckboxes && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox 
                      checked={selectedReceivables.includes(receivable.id)}
                      onCheckedChange={() => toggleSelectReceivable(receivable.id)}
                      aria-label={t("table.selectRow", { id: receivable.remittanceInformation })}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex items-center">
                    {getStatusIcon(receivable.status)}
                    <Badge variant={status.variant}>{status.label}</Badge>
                    <PaymentVerificationBadge paymentVerification={receivable.paymentVerification} />
                  </div>
                </TableCell>
                <TableCell className={isHighlighted ? "font-semibold" : ""}>
                  {receivable.remittanceInformation}
                </TableCell>
                <TableCell>
                  {receivable.customer}
                  {receivable.tax && (
                    <Badge variant="outline" className="ml-2">
                      {taxCaption(receivable.tax.transactionType, receivable.tax.description)}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{formatCurrency(receivable.amount, receivable.currency)}</TableCell>
                <TableCell>{receivable.currency}</TableCell>
                <TableCell>{formatOptionalDate(receivable.dueDate)}</TableCell>
                <TableCell>{renderMatchStatus(receivable)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="cursor-pointer hover:bg-muted p-1 rounded-sm focus:outline-none">
                      <MoreHorizontal className="h-5 w-5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="z-50 bg-background"
                      // Items here open dialogs: returning focus to this trigger as the menu closes
                      // would race the dialog's own focus trap, which re-selects its first field and
                      // replaces whatever the user has started typing there (FIN-1123).
                      onCloseAutoFocus={(event) => event.preventDefault()}
                    >
                      <DropdownMenuItem onClick={() => handleView(receivable.id)} className="flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        <span>{t("actions.viewDetails")}</span>
                      </DropdownMenuItem>
                      {/* #498 Task 8 — correction/reopen path (design spec "Correction path" UX
                          Requirement, AC14, User Flow 7). Investigation (see task-8-report.md)
                          confirmed this menu previously hid "Mark as received" entirely once a
                          receivable reached 'paid'/'manually_handled' — the only way to fix a
                          mistaken date/method/match was to delete and recreate the record.
                          Reopening reuses the SAME MarkAsReceivedModal (no separate "edit" mode)
                          — it already shows the previously entered details and produces a NEW
                          auto_match_decisions row on confirm (append-only, never mutates the
                          original decision).

                          #498 Task 8 REVIEW FIX — deliberately excludes 'paid' (the review's
                          Finding 4 scoping decision, applied here too — see
                          InvoiceActionMenu.tsx's identical comment for the payable case). Unlike
                          payables (which have NO JIT scoring path at all), receivables DO have a
                          live JIT fallback on "Search again" (matchingService.ts's
                          getTransactionSuggestionsForReceivable) — but that JIT function ITSELF
                          short-circuits to `return []` when `receivable.matched` is already true
                          (see its own early-return guard), so reopening a genuinely-matched
                          ('paid') receivable hits the exact same zero-candidates dead end as
                          payables do, funneling into the acknowledgment checkbox and contradicting
                          the still-standing real-match audit row. Scoped to 'manually_handled'
                          only — status==='paid' for receivables is set EXCLUSIVELY by a real
                          manual_matches/transaction match (see receivableService.ts's
                          getMatchingInfo/getReceivables — 'manually_handled' rows are never
                          overridden to 'paid'), so this mirrors the payable case exactly. */}
                      {/* FIN-977: a payout receivable is settled by its payout row, never by hand. */}
                      {onMarkAsReceived && !receivable.tax && (
                        receivable.status === "unpaid"
                        || receivable.status === "overdue"
                        || receivable.status === "manually_handled"
                      ) && (
                        <DropdownMenuItem onClick={() => onMarkAsReceived(receivable)} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          <span>{t("actions.markAsReceived")}</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
          {sortedReceivables.length === 0 && (
            <TableRow>
              <TableCell colSpan={hideCheckboxes ? 8 : 9} className="h-24 text-center">
                {filteredReceivables.length === 0 
                  ? t("table.noMatch")
                  : t("table.none")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReceivablesTable;
